import { Location, NgIf, NgClass, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isProductAvailable } from '@woolworthsnz/product';
import {
	AlertComponent,
	AlertType,
	AppSettingsService,
	ButtonComponent,
	ButtonState,
	DatalayerService,
	EmbeddedVisibilityDirective,
	HistoryService,
	RoutedModalBaseComponent,
	SlottedModalComponent,
	WappleLoadingComponent,
} from '@woolworthsnz/styleguide';
import { IngredientProduct, ProductResponse, TrolleyItemRequest } from '@woolworthsnz/trader-api';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { RecipeService } from '../../services';
import { GroupedIngredients, orderAndGroupIngredients } from '../helpers/ingredients-mapper';
import { formatServingsText } from '../helpers/servings-formatter';
import { IngredientGridComponent } from '../ingredient-grid/ingredient-grid.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
export const RECIPE_ADD_INGREDIENTS_MODAL_PATH = 'recipe-add-ingredients';

@Component({
	selector: 'recipe-add-ingredients-modal',
	templateUrl: './add-ingredients-modal.component.html',
	styleUrls: ['./add-ingredients-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		SlottedModalComponent,
		EmbeddedVisibilityDirective,
		NgClass,
		IngredientGridComponent,
		AlertComponent,
		ButtonComponent,
		WappleLoadingComponent,
		AsyncPipe,
	],
})
export class AddIngredientsModalComponent extends RoutedModalBaseComponent implements OnDestroy {
	@ViewChild(SlottedModalComponent) modalComponent: SlottedModalComponent;
	private destroyed$: Subject<boolean> = new Subject();

	title?: string;

	ingredients?: (IngredientProduct & { desiredQuantity: number })[];
	pantryItems?: (IngredientProduct & { desiredQuantity: number })[];
	servings?: string;

	loaded$ = new BehaviorSubject<boolean>(false);
	selectedSkus$ = new BehaviorSubject<string[]>([]);
	selectedPantrySkus$ = new BehaviorSubject<string[]>([]);
	buttonState$ = new BehaviorSubject(ButtonState.default);
	addToTrolleyNotification$ = new Subject<{ message: string; type: AlertType } | undefined>();
	closeTimeout: ReturnType<typeof setTimeout>;
	buttonDisabled$: Observable<boolean>;

	constructor(
		protected router: Router,
		protected historyService: HistoryService,
		private recipeService: RecipeService,
		private route: ActivatedRoute,
		private location: Location,
		private datalayerService: DatalayerService,
		private appSettingsService: AppSettingsService
	) {
		super(router, route, historyService);
		const state = this.router.getCurrentNavigation()?.extras.state;

		if (state?.ingredients) {
			this.initialise(state.ingredients, state.title, state.servings);
			return;
		}

		// load from params if router state is not available
		this.route.params
			.pipe(
				map((p) => p.id),
				filter((id) => !!id),
				switchMap((id) => this.recipeService.getRecipeDetail(id)),
				takeUntilDestroyed(),
				tap((recipe) =>
					this.initialise(orderAndGroupIngredients(recipe.ingredients), recipe.title, recipe.servings)
				)
			)
			.subscribe();
	}

	get isEmbeddedInApp(): boolean {
		return this.appSettingsService.getSetting('isEmbeddedInApp');
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	addToTrolley(): void {
		/**
		 * Note: It's important that the ingredients and pantry items are retrieved individually. If the same sku exists in both the quantity added would be doubled inadvertently if the lists were joined before getting products
		 */
		const ingredientProducts = this.getSelectedProducts([...this.selectedSkus$.getValue()], this.ingredients);
		const pantryProducts = this.getSelectedProducts([...this.selectedPantrySkus$.getValue()], this.pantryItems);
		let itemsToAdd: TrolleyItemRequest[] = ingredientProducts.map((item) => ({
			sku: item.sku,
			quantity: item.desiredQuantity,
			pricingUnit: item.unit,
			quantityType: TrolleyItemRequest.QuantityTypeEnum.AddToQuantity,
		}));

		itemsToAdd = itemsToAdd.concat(
			pantryProducts.map((item) => ({
				sku: item.sku,
				quantity: item.desiredQuantity,
				pricingUnit: item.unit,
				quantityType: TrolleyItemRequest.QuantityTypeEnum.AddToQuantity,
			}))
		);

		this.buttonState$.next(ButtonState.busy);

		this.recipeService
			.addIngredientsToTrolley(itemsToAdd)
			.pipe(takeUntil(this.destroyed$))
			.subscribe(
				(res) => {
					const partialError = res.failedItems?.length;
					this.buttonState$.next(ButtonState.completed);

					if (partialError) {
						this.addToTrolleyNotification$.next({
							message: 'Not all items were added to your trolley, please check your trolley',
							type: AlertType.warning,
						});
						return;
					}

					this.trackAddToTrolleyEvent([...ingredientProducts, ...pantryProducts], itemsToAdd);

					// automatically close when there's no partial errors after 1 second
					this.closeTimeout = setTimeout(() => this.close(), 1000);
				},
				() => {
					this.addToTrolleyNotification$.next({
						message: 'There was an issue encountered when adding to trolley, please try again later',
						type: AlertType.error,
					});

					this.buttonState$.next(ButtonState.errored);
				}
			);
	}

	close(): void {
		if (this.closeTimeout) {
			clearTimeout(this.closeTimeout);
		}
		this.location.back();
	}

	handleMainSelectionChanges(event: string[]): void {
		this.selectedSkus$.next(event);
		this.modalComponent.forceRedraw();
	}

	handlePantrySelectionChanges(event: string[]): void {
		this.selectedPantrySkus$.next(event);
		this.modalComponent.forceRedraw();
	}

	handleQuantityChanged({ sku, quantity }: { sku: string; quantity: number }): void {
		const ingredient = this.ingredients?.find((i) => i.item?.sku === sku);

		if (ingredient) {
			ingredient.desiredQuantity = quantity;
			this.syncSelection(ingredient.item?.sku || '', ingredient.desiredQuantity, this.selectedSkus$);
		} else {
			const pantryItem = this.pantryItems?.find((i) => i.item?.sku === sku);
			if (pantryItem) {
				pantryItem.desiredQuantity = quantity;
				this.syncSelection(pantryItem.item?.sku || '', pantryItem.desiredQuantity, this.selectedPantrySkus$);
			}
		}
	}

	private syncSelection(sku: string, quantity: number, selectedSkus$: BehaviorSubject<string[]>): void {
		if (quantity === 0) {
			selectedSkus$.next([...selectedSkus$.getValue().filter((v) => v !== sku)]);
		} else if (selectedSkus$.getValue().indexOf(sku) < 0) {
			selectedSkus$.next([...selectedSkus$.getValue(), sku]);
		}
	}

	private initialise(ingredients: GroupedIngredients[], title?: string, servings?: string): void {
		this.title = title;
		this.servings = formatServingsText(servings);
		this.ingredients = this.getProductItems(ingredients);
		this.pantryItems = this.getProductItems(ingredients, ['Pantry', 'Wine']);
		this.selectedSkus$.next(this.getSelectedSkus());
		this.loaded$.next(true);

		this.datalayerService.trackProductImpressions([
			...this.ingredients.map((i) => i.item || <ProductResponse>{}),
			...this.pantryItems.map((i) => i.item || <ProductResponse>{}),
		]);

		this.buttonDisabled$ = combineLatest([this.selectedSkus$, this.selectedPantrySkus$]).pipe(
			map(([mainIngredientSkus, pantrySkus]) => {
				const main = this.getSelectedProducts(mainIngredientSkus, this.ingredients);
				const pantry = this.getSelectedProducts(pantrySkus, this.pantryItems);
				return !(main.length || pantry.length);
			})
		);
	}

	private getProductItems(ingredientGroups: GroupedIngredients[], filterOnType: string[] = ['MainMeal']): any[] {
		const items = ingredientGroups.map((group) =>
			group.ingredients
				.filter(
					(ingredient) =>
						!!ingredient?.item && filterOnType.indexOf(ingredient.ingredientType || 'MainMeal') > -1
				)
				.map((i) => ({ ...i, desiredQuantity: i.item?.quantity?.value || 0 }))
		);

		// flatten items
		return ([] as (IngredientProduct & { desiredQuantity: number })[]).concat(...items);
	}

	private getSelectedSkus(): any[] {
		if (!this.ingredients) {
			return [];
		}
		return this.ingredients.map((product) => product.item?.sku as string);
	}

	private trackAddToTrolleyEvent(products: ProductResponse[], itemsAdded: TrolleyItemRequest[]): void {
		const productAddedEvents = itemsAdded.map((item) => {
			const product = products.find((p) => p.sku === item.sku);
			return {
				id: item.sku,
				price: product?.price?.salePrice || 0,
				quantity: item.quantity,
				saleUnit: item.pricingUnit,
				carouselName: undefined,
				specialLabel: product?.productTag?.tagType,
				cartologySponsoredProduct: product?.adId ? 'Yes' : undefined,
			};
		});

		this.datalayerService.trackRecipeAddToTrolley(productAddedEvents);
	}

	private getSelectedProducts(
		selectedSkus: string[],
		list: (IngredientProduct & { desiredQuantity: number })[] = []
	): any[] {
		return list
			.filter((product) => product?.item?.price?.salePrice && isProductAvailable(product.item))
			.filter((item) => !!item.item?.sku && selectedSkus.includes(item.item?.sku))
			.map((item) => {
				const r = { ...item.item } as ProductResponse & { sku: string; desiredQuantity: number };
				r.desiredQuantity = item.desiredQuantity;
				return r;
			});
	}
}
